import $ from 'jquery';

import Navigation from '../components/Navigation';
import Goal from '../components/Goal';
import ThemeUtils from '../utils/ThemeUtils';

export default class StaticHeader {
  constructor(instance) {
    // Base Selectors
    this.window = $(window);
    this.$document = $(document.body);
    this.$el = $(instance.el);

    this.themeUtils = ThemeUtils;

    this.windowWidth = ThemeUtils.windowWidth();
    this.$drawerMenu = this.$document.find('[data-header-drawer]');
    this.$drawerToggle = this.$el.find('[data-drawer-toggle]');
    this.$mainContent = $('[data-main-content]');
    this.$headerContainer = this.$el.find('[data-main-header]');
    this.$headerContent = this.$el.find('[data-header-content]');
    this.$headerLogo = this.$el.find('[data-header-logo]');
    this.$headerGoal = this.$el.find('.module-header-goal-wrapper');
    this.$searchWrapper = this.$el.find('[data-header-search]');

    this.headerContentWidth = 0;

    this.slideShow = '.static-slideshow-wrapper';
    this.$slideShow = $(this.slideShow);

    this.$document
      .on('shopify:section:select', () => this.sectionChanges(null))
      .on('shopify:section:deselect', () => this.sectionChanges(null))
      .on('click.search', '[data-search-toggle]', () => this._openSearch())
      .on('blur.search', '.header-search-input', () => this._closeSearch())
      .on('click.drawer', '[data-drawer-toggle]', event => this._toggleDrawerNav(event))
      .on('calculateHeaderWidths', () => this._calculateHeaderWidths())
      .on('toggleStickyHeader', (event, $slideshow) => this.stickyHeader($slideshow))
      .on('toggleSlideShowHeader', (event, $slideshow) => this.slideShowHeader($slideshow))
      .on('checkAnnouncementBar', (event, data) => this._checkAnnouncementBar(data))
      .on('checkHeaderOffsets', () => this._headerOffsets());

    this.window.on('resize.header', () => this._resizeEvents());

    // This can't be debounced as it creates a visual delay
    this.window.on('scroll.header', () => this._headerOffsets());

    // Conditionals
    this.isHeaderNavigation = this.$headerContainer.attr('data-main-header') === 'header';
    this.hasLogo = this.$headerLogo.length;
    this.isHeaderSticky = (this.$headerContainer.attr('data-sticky-header') != null);

    // Imported Classes
    this.navigation = new Navigation({ el: this.$document });

    // PxsAnnouncementBar connection
    this.announcementBarEl = null;
    this.$announcementBarEl = null;
    this.announcementBarRect = {};

    this._checkAnnouncementBar({
      el: document.querySelector('.pxs-announcement-bar'),
    });

    if (this.$headerGoal.length) {
      this.goal = new Goal({ el: this.$headerGoal });
    }

    if (this.isHeaderNavigation) {
      this._calculateHeaderWidths();
    }

    this.sectionChanges(this.$slideShow);
    this._headerOffsets();
  }

  onSectionUnload() {
    this.$document
      .off('.header-search-toggle')
      .off('click.search', '[data-search-toggle]')
      .off('blur.search', '.header-search-input')
      .off('click.drawer', '[data-drawer-toggle]')
      .off('calculateHeaderWidths')
      .off('toggleStickyHeader')
      .off('toggleSlideShowHeader')
      .off('checkAnnouncementBar')
      .off('checkHeaderOffsets');

    // Remove Goal logic when section unloads
    if (this.goal != null) {
      this.goal.remove();
    }

    this.window.off('resize.header');
    this.window.off('scroll.header');
  }

  _resizeEvents() {
    // Alternate logo has potential to be a different width
    this._fitHeader();
    this._headerOffsets();
    const currentWindowWidth = this.themeUtils.windowWidth();

    // Only close navigation if width has changed
    if ((this.$document.hasClass('showing-drawer')) && (currentWindowWidth !== this.windowWidth)) {
      this.windowWidth = this.themeUtils.windowWidth();
      this._toggleDrawerNav();
    }
  }

  /*
    These events need to fire when any section in the TE is reloaded
  */
  sectionChanges($slideshow) {
    this.stickyHeader($slideshow);
    this.slideShowHeader($slideshow);
  }

  stickyHeader($slideShow) {
    if ($slideShow == null) { $slideShow = $(this.slideShow); }
    this.$document.toggleClass('has-sticky-header', this.isHeaderSticky);
    this._headerOffsets($slideShow);
  }

  _headerOffsets($slideShow) {
    if ($slideShow == null) { $slideShow = $(this.slideShow); }
    const isFullWindowSlideshow = ($slideShow.attr('data-full-window-slideshow') != null);
    const { pageYOffset } = window;
    let scrollThreshold = 0;
    let headerOffset = '';
    let bodyOffset = '';

    if (this.announcementBarEl) {
      // Scroll related styles only apply when scrolled past AnnouncementBar
      scrollThreshold = this.announcementBarRect.height;
    }

    const pastThreshold = pageYOffset > scrollThreshold;

    // Adjust the header in situations where it is absolute
    if (this.isHeaderSticky || isFullWindowSlideshow) {
      // When full window slideshow, @$headerContainer is 'top: 0'
      if (isFullWindowSlideshow && this.announcementBarEl) {
        headerOffset = this.announcementBarRect.height;
        bodyOffset = this.announcementBarRect.height;
      }

      if (this.isHeaderSticky) {
        // Offset absolute positioning to top of viewport, or below  AnnouncementBar
        headerOffset = pastThreshold ? 0 : scrollThreshold;

        if (!isFullWindowSlideshow) {
          bodyOffset = scrollThreshold + this.$headerContent.outerHeight(true);

          // If header is sticky, and no announcement bar, no offset
          if (this.themeUtils.windowWidth() < 720) {
            bodyOffset = 0;
          }

          // If there is an announcement bar, offset by this its height
          if ((this.themeUtils.windowWidth() < 720) && this.announcementBarEl) {
            bodyOffset = this.announcementBarRect.height;
          }
        }

        if (this.announcementBarEl && !pastThreshold) {
          const bottomEdge = scrollThreshold - pageYOffset;
          headerOffset = Math.max(bottomEdge, 0);
        }

        if (this.$headerGoal.length && this.$headerGoal.hasClass('active')) {
          if (this.themeUtils.windowWidth() < 720) {
            this.$headerGoal.css('top', '');
          } else {
            const goalHeight = this.$headerGoal.height();
            this.$headerGoal.css('top', headerOffset);
            bodyOffset += goalHeight;
            headerOffset += goalHeight;
          }
        }
      }
    }

    if (this.themeUtils.windowWidth() < 720) {
      headerOffset = '';
    }

    this.$document.css({ paddingTop: bodyOffset });
    this.$headerContainer
      .toggleClass('scrolled', pastThreshold && this.isHeaderSticky)
      .css({ top: headerOffset });

    if ($slideShow.length) {
      $slideShow.trigger('setSlideshowClasses');
    }

    if (isFullWindowSlideshow && this.isHeaderSticky) {
      this.$headerContainer
        .toggleClass('higher-than-slideshow', !pastThreshold);
      this.$headerContainer
        .toggleClass('lower-than-slideshow', pastThreshold);
    }
  }

  slideShowHeader($slideShow) {
    if ($slideShow == null) { $slideShow = $(this.slideShow); }
    if ($slideShow.length && ($slideShow.attr('data-full-window-slideshow') != null)) {
      this.$headerContainer.addClass('higher-than-slideshow');
      this.$document.addClass('has-slideshow-full-window');
    } else {
      this.$headerContainer.removeClass('higher-than-slideshow');
      this.$document.removeClass('has-slideshow-full-window');
    }
  }

  _calculateHeaderWidths() {
    let brandingWidth = this.$el.find('.branding').outerWidth(true);
    const toolsWidth = this.$el.find('.header-tools').outerWidth(true);

    /*
      Nav links have 15px left/right for 30px.
      First item only has 15px, this makes the first item equidistant from the logo
    */
    const gutterWidth = 15;

    if (this.$headerLogo.length) {
      const logoWidths = [];

      this.$headerLogo.each((index, logo) => {
        logoWidths.push($(logo).width());
      });

      brandingWidth = Math.max.apply(Math, logoWidths);
    }

    this.headerContentWidth = Math.ceil(brandingWidth) + Math.ceil(toolsWidth) + gutterWidth;

    this._fitHeader();
  }

  _fitHeader() {
    const headerWidth = this.$headerContent.width();

    this.$headerContent.toggleClass('collapsed-navigation', this.headerContentWidth >= headerWidth);
  }

  _toggleDrawerNav(event = null) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const isDrawerOpen = this.$document.hasClass('showing-drawer');

    this.$document.toggleClass('showing-drawer', !isDrawerOpen);

    if (isDrawerOpen) {
      // Drawer is going to close, store existing top
      const top = this.$headerContainer.data('old-top');
      if (top) {
        this.$headerContainer.css({ top });
      } else {
        this._headerOffsets();
      }
    } else {
      this.$headerContainer.data('old-top', this.$headerContainer.css('top'));
      this.$headerContainer.css('top', '');
    }

    this.$headerContainer.one('transitionend', () => {
      this.$document.toggleClass('drawer-visible', !isDrawerOpen);
      this.$drawerToggle.attr('aria-expanded', !isDrawerOpen);
    });
  }

  _openSearch() {
    if (window.innerWidth <= 720) {
      window.location.href = '/search';
      return;
    }

    this.$el.find('.header-search-wrapper').addClass('active').find('input').focus();

    this.$el.find('.header-search-wrapper').on('keyup.search', (event) => {
      if (event.keyCode === 27) {
        this._closeSearch();
      }
    });
  }

  _closeSearch() {
    this.$el.find('.header-search-wrapper').removeClass('active').off('keyup.search');
  }

  _checkAnnouncementBar(data) {
    const { el } = data;

    // Refresh globals when AnnouncementBar is updated
    this.announcementBarEl = el;
    this.$announcementBarEl = $(this.announcementBarEl);
    this.announcementBarRect = this.announcementBarEl != null
      ? this.announcementBarEl.getBoundingClientRect()
      : undefined;

    this._headerOffsets();
  }
}
